import {
	Component,
	OnDestroy,
	OnInit,
	Input, Output, EventEmitter
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Roles } from '@eview/core/models/roles';
import { selectRoles } from '@eview/core/store/selectors/roles.selector';
import { AppState } from '@eview/core/store/states/app.state';
import { Permission } from '@eview/core/auth/permission';
import {
	UntypedFormGroup,
	UntypedFormBuilder,
	Validators
} from '@angular/forms';
import { AuthHelpers } from '@eview/core/auth';
import { AllowedPriviledge } from '@eview/core/auth/auth';
import { UsersHelpers } from '@eview/core/users/users.helpers';
import { filter } from 'rxjs/operators';
import { Observable, Subscription, of, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { UserService } from '@eview/core/users/user.service';
import { selectMap } from '@eview/core/store/selectors/map.selector';
import { ContactType } from '@eview/core/models/user';
import { SearchableSelectOption } from '@eview/features/ui/searchable-select/searchable-select.component';
import { RoleItem } from '@eview/core/models/roles';
import { PostSource } from '@eview/core/domain/post/post';
import { ToastType } from '../../toast/toast.component';
import { ToastService } from '../../toast/toast.service';
import { isEmpty } from 'lodash';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { executeRecaptcha } from '@eview/core/auth/recaptcha.helpers';
import { GetRegionData } from '@eview/core/store/actions/dashboard.actions';

import { passwordExpiredValidator } from '../validators/password-expired.validator';
import { inputTextValidator } from '../validators/inputText.validator';

@Component({
	selector: 'eview-user-signup',
	templateUrl: 'user-signup.component.html',
	styleUrls: ['user-signup.component.scss']
})

export class UserSignupComponent implements OnInit, OnDestroy {

	@Input() isEditing: boolean;
	@Input() userData: any;
	@Input() editId: any;
	@Output() onModalClose: EventEmitter<Boolean> = new EventEmitter();
	// @Output() onCancelClick: EventEmitter<Boolean> = new EventEmitter();
	@Input() isAnonymous: boolean;
	@Input() isApproved: boolean;
	@Input() changePassword: boolean = false;

	AuthHelpers = AuthHelpers;
	AllowedPriviledge = AllowedPriviledge;
	Permission = Permission;
	UsersHelpers = UsersHelpers;

	subs: Subscription;
	userForm: UntypedFormGroup;
	regions: any[];
	roles$: Observable<Roles>;
	showPassword: boolean = false;

	roleSelectOptions(roles: RoleItem[]): SearchableSelectOption[] {
		return (roles || []).map(role => ({
			value: role.name,
			label: role.name
		}));
	}

	get regionSelectOptions(): SearchableSelectOption[] {
		return (this.regions || []).map(region => ({
			value: region.value,
			label: region.name
		}));
	}

	constructor(
		private store: Store<AppState>,
		private userService: UserService,
		private formBuilder: UntypedFormBuilder,
		private toast: ToastService,
		private recaptchaV3Service: ReCaptchaV3Service
	) {
		this.subs = new Subscription();
		this.roles$ = this.store
			.select(selectRoles)
			.pipe(filter(roles => roles !== null));
	}

	ngOnInit() {
		if (this.changePassword) { this.createChangePasswordForm(); }
		else {
			this.createUserForm();
			// Disparar la carga de regiones
			this.store.dispatch(new GetRegionData());
			this.getRegions();
		}
		this.onEditClick();

	}

	ngOnDestroy() {
		this.userForm.reset();
		this.subs.unsubscribe();
	}

	createUserForm() {
		let items: any = {
			realname: ['', [Validators.required, inputTextValidator, Validators.maxLength(150)]],
			email: ['', [Validators.required, Validators.email, Validators.maxLength(150)]],
			role: [null, (this.isAnonymous) ? Validators.nullValidator : [Validators.required, this.notNullStringValidator]],
			organization: ['', [Validators.nullValidator, inputTextValidator, Validators.maxLength(150)]],
			position: ['', [Validators.nullValidator, inputTextValidator, Validators.maxLength(150)]],
			contact: ['', [Validators.nullValidator, Validators.minLength(8), Validators.pattern(/^\+[1-9]\d{1,14}$/)]],
			mgmt_lev_1: [null, Validators.nullValidator],
			token: ""
		};
		
		if (!this.isEditing) {
			items.password = ['', [Validators.required, passwordExpiredValidator]];
		}
		
		this.userForm = this.formBuilder.group(items);
	}

	createChangePasswordForm() {
		this.userForm = this.formBuilder.group({
			//password	    : ['', [(this.isAnonymous) ? Validators.required : Validators.nullValidator, Validators.minLength(7)]]
			password: ['', [
				Validators.required,
				passwordExpiredValidator
			]]
		});
	}

	onEditClick() {
		const user = this.userData;
		if (!isEmpty(user)) {
			this.userForm.patchValue({
				realname: user.realname,
				email: user.email,
				role: user.role || null,
				mgmt_lev_1: (user.mgmt_lev_1 == "null" || user.mgmt_lev_1 == "") ? null : user.mgmt_lev_1,
				organization: user.organization,
				position: user.position
			});
		}
	}

	getRegions() {
		console.log('Getting regions from map store...');
		this.subs.add(
			this.store.select(selectMap).subscribe(map => {
				console.log('Map data received:', map);
				if (map && map.regions) {
					console.log('Map regions:', map.regions);
					this.regions = Object.keys(map.regions).map(region => { return { value: region, name: region } });
					console.log('Processed regions:', this.regions);
				} else if (map && map.departments) {
					console.log('Map departments:', map.departments);
					this.regions = Object.keys(map.departments).map(dept => { return { value: dept, name: dept } });
					console.log('Processed departments:', this.regions);
				} else {
					console.log('No map data found, loading from dashboard service');
					this.loadRegionsFromAPI();
				}
			})
		);
	}

	loadRegionsFromAPI() {
		// Importar DashboardService y GetRegionData
		import('@eview/core/domain/dashboard/dashboard.service').then(({ DashboardService }) => {
			const dashboardService = new DashboardService(null, null);
			// Fallback: usar regiones hardcodeadas si no se puede cargar desde API
			this.regions = [
				{ value: 'Amazonas', name: 'Amazonas' },
				{ value: 'Áncash', name: 'Áncash' },
				{ value: 'Apurímac', name: 'Apurímac' },
				{ value: 'Arequipa', name: 'Arequipa' },
				{ value: 'Ayacucho', name: 'Ayacucho' },
				{ value: 'Cajamarca', name: 'Cajamarca' },
				{ value: 'Callao', name: 'Callao' },
				{ value: 'Cusco', name: 'Cusco' },
				{ value: 'Huancavelica', name: 'Huancavelica' },
				{ value: 'Huánuco', name: 'Huánuco' },
				{ value: 'Ica', name: 'Ica' },
				{ value: 'Junín', name: 'Junín' },
				{ value: 'La Libertad', name: 'La Libertad' },
				{ value: 'Lambayeque', name: 'Lambayeque' },
				{ value: 'Lima', name: 'Lima' },
				{ value: 'Loreto', name: 'Loreto' },
				{ value: 'Madre de Dios', name: 'Madre de Dios' },
				{ value: 'Moquegua', name: 'Moquegua' },
				{ value: 'Pasco', name: 'Pasco' },
				{ value: 'Piura', name: 'Piura' },
				{ value: 'Puno', name: 'Puno' },
				{ value: 'San Martín', name: 'San Martín' },
				{ value: 'Tacna', name: 'Tacna' },
				{ value: 'Tumbes', name: 'Tumbes' },
				{ value: 'Ucayali', name: 'Ucayali' }
			];
			console.log('Using hardcoded regions:', this.regions);
		});
	}

	async generateUserExtra($params) {
		let extraInfo = {
			user_id: $params['info']["id"],
			region: $params['info']['region'],
			organization: $params['info']['organization']
		}
		let extraResponse = await Promise.resolve(this.userService.saveUserExtra(extraInfo).toPromise());
		this.isEditing = false;
		this.userForm.reset();
	}

	onSubmitClick() {
		let msgTemplate = 'TOAST_CREATE_USER';
		let errorTemplate = 'TOAST_CREATE_USER_ERROR';
		if (!this.isEditing) {
			let reqBody = this.userForm.value;
			reqBody.mgmt_lev_1 = (reqBody.mgmt_lev_1 == "null" || reqBody.mgmt_lev_1 == "") ? null : reqBody.mgmt_lev_1;
			if (this.isAnonymous) {
				delete reqBody.role;
				delete reqBody.contact;
			}
			this.subs.add(
				executeRecaptcha(this.recaptchaV3Service).subscribe((token) => {
					reqBody.token = token;
					this.userService.register(reqBody)
						// .pipe(
						// 	catchError((err) => {
						// 		if (err && err.statusText) {
						// 			this.toast.show(ToastType.Danger, err.statusText);
						// 		}
						// 		return throwError(err);
						// }))
						.subscribe(async response => {
							if (undefined !== response["error"]) {
								this.toast.show(ToastType.Danger, response["error"]["message"]);
							} else {
								this.toast.show(ToastType.Success, msgTemplate);
								this.generateUserExtra({ info: { ...response, ...reqBody }, isEditing: this.isEditing });
								this.onModalClose.next(false);
								this.onContactSubmitClick(response.id);
							}
						})
				})
			);
		} else {
			const reqBody = this.userForm.value;
			msgTemplate = 'TOAST_UPDATE_USER';
			if (reqBody && isEmpty(reqBody.password)) {
				delete reqBody.password;
			}
			if (reqBody && (reqBody.mgmt_lev_1 == "null" || reqBody.mgmt_lev_1 == "")) {
				reqBody.mgmt_lev_1 = null;
			}
			this.subs.add(
				this.userService.updateUser(reqBody, this.editId).subscribe(
					async response => {
						this.generateUserExtra({ info: { ...response, ...reqBody }, isEditing: this.isEditing });
						this.onModalClose.next(false);
					}
				)
			);
			this.toast.show(ToastType.Success, msgTemplate);
		}
	}

	onContactSubmitClick(userId) {
		const { contact } = this.userForm.value;
		if (!isEmpty(contact)) {
			let reqBody = {
				id: 1,
				type: ContactType.Phone,
				user_id: userId,
				data_source: PostSource.Web,
				contact: contact
			};
			this.userService.addContacts(reqBody).subscribe(results => {
				console.log("Contact has been updated");
			});
		}
	}
	validarField(event: KeyboardEvent) {
		const char = event.key;
		const target = event.target as HTMLInputElement;
		const currentValue = target.value;
		
		// Para email: prevenir escritura si excede 150 caracteres
		if (target.getAttribute('formControlName') === 'email' && currentValue.length >= 150) {
			event.preventDefault();
			return;
		}
		
		// Para realname, organization, position y contact: prevenir caracteres no permitidos al inicio (excepto diéresis)
		if (['realname', 'organization', 'position'].includes(target.getAttribute('formControlName'))) {
			// Prevenir escritura si excede 150 caracteres
			if (currentValue.length >= 150 && !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight'].includes(char)) {
				event.preventDefault();
				return;
			}
			const selectionStart = target.selectionStart || 0;
			if (selectionStart === 0 && /[\s.\-'"°]/.test(char)) {
				event.preventDefault();
				return;
			}
			
			// Caracteres válidos según el campo
			const fieldName = target.getAttribute('formControlName');
			let regex;
			if (fieldName === 'realname') {
				regex = /^[a-zA-Z0-9áéíóúÁÉÍÓÚüÜñÑ\s.\-"°]$/;
			} else {
				regex = /^[a-zA-Z0-9áéíóúÁÉÍÓÚüÜñÑ\s.\-'"°]$/;
			}
			if (!regex.test(char)) {
				event.preventDefault();
				return;
			}
		}
		
		// Para otros campos
		const regex = /^[a-zA-Z0-9áéíóúüÁÉÍÓÚÜñÑ\s\-_@.']$/;
		if (!regex.test(char)) {
			event.preventDefault();
		}
	}

	onPaste(event: ClipboardEvent, fieldName: string) {
		event.preventDefault();
		const pastedText = event.clipboardData?.getData('text') || '';
		let cleanedText = pastedText.trimStart().substring(0, 150);
		
		if (['realname', 'organization', 'position'].includes(fieldName)) {
			// Filtrar solo caracteres permitidos
			if (fieldName === 'realname') {
				cleanedText = cleanedText.replace(/[^a-zA-Z0-9áéíóúÁÉÍÓÚüÜñÑ\s.\-"°]/g, '');
			} else {
				cleanedText = cleanedText.replace(/[^a-zA-Z0-9áéíóúÁÉÍÓÚüÜñÑ\s.\-'"°]/g, '');
			}
			// Remover caracteres no permitidos al inicio (excepto diéresis)
			cleanedText = cleanedText.replace(/^[\s.\-'"°]+/, '');
		} else if (fieldName === 'email') {
			// Filtrar solo caracteres permitidos para email
			cleanedText = cleanedText.replace(/[^a-zA-Z0-9@._-]/g, '');
		}
		
		this.userForm.get(fieldName)?.setValue(cleanedText);
	}

	noWhitespaceValidator(control: any) {
		const value = control.value || '';
		const isWhitespace = value.trim().length === 0;
		const hasSpaces = value.includes(' ');
		return (!isWhitespace && !hasSpaces) ? null : { 'whitespace': true };
	}

	onEmailInput(event: any) {
		const emailRegex = /^[a-zA-Z0-9@._-]*$/;
		const value = event.target.value;
		if (!emailRegex.test(value)) {
			event.target.value = value.replace(/[^a-zA-Z0-9@._-]/g, '');
			this.userForm.patchValue({ email: event.target.value });
		}
	}

	onEmailKeypress(event: KeyboardEvent) {
		const target = event.target as HTMLInputElement;
		const currentValue = target.value;
		
		// Prevenir escritura si excede 150 caracteres
		if (currentValue.length >= 150 && !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
			event.preventDefault();
			return;
		}
		
		const emailRegex = /[a-zA-Z0-9@._-]/;
		if (!emailRegex.test(event.key) && !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight'].includes(event.key)) {
			event.preventDefault();
		}
	}

	togglePasswordVisibility() {
		this.showPassword = !this.showPassword;
	}

	onContactKeyPress(event: KeyboardEvent) {
		const char = event.key;
		// Solo números y símbolo +
		if (!/[0-9+]/.test(char) && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(char)) {
			event.preventDefault();
		}
	}

	onCancel() {
		this.onModalClose.next(false);
	}

	// Validador personalizado para evitar que "null" como string sea válido
	notNullStringValidator(control: any) {
		const value = control.value;
		return (value === null || value === 'null' || value === '') ? { 'required': true } : null;
	}
}